import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, RectangleHorizontal, RectangleVertical } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useMultiCameraStore } from "@/shared/stores/useMultiCameraStore";
import VideoPreviewEnhanced, { type VideoOrientation } from "./video-preview-enhanced";

interface SecondaryCameraProps {
  device_mxid: string;
  machineId: number;
  /** Initial orientation (defaults to horizontal). */
  defaultOrientation?: VideoOrientation;
}

/**
 * An extra camera angle synced to the shared timeline cursor (`followerMode`).
 * Used by machine-detail and the `/map` video panel. Hover reveals an
 * orientation toggle and a remove button (clears it from `useMultiCameraStore`).
 */
const SecondaryCamera = ({
  device_mxid,
  machineId,
  defaultOrientation = "horizontal",
}: SecondaryCameraProps) => {
  const { t } = useTranslation("machines");
  const [orientation, setOrientation] = useState<VideoOrientation>(defaultOrientation);
  const { removeCamera } = useMultiCameraStore();

  return (
    <div className="relative w-full rounded-xl overflow-hidden group">
      {/* Overlay controls — visible on hover */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
          onClick={() => setOrientation(orientation === "vertical" ? "horizontal" : "vertical")}
          title={orientation === "vertical" ? t("video.switchHorizontal") : t("video.switchVertical")}
        >
          {orientation === "vertical" ? (
            <RectangleHorizontal className="h-3 w-3" />
          ) : (
            <RectangleVertical className="h-3 w-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 bg-black/60 hover:bg-destructive text-white"
          onClick={() => removeCamera(machineId, device_mxid)}
          title={t("video.removeCamera")}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <VideoPreviewEnhanced
        device_mxid={device_mxid}
        orientation={orientation}
        onOrientationChange={setOrientation}
        followerMode
      />
    </div>
  );
};

export default SecondaryCamera;
